<template>
  <v-dialog
    v-model="dialog"
    persistent
    transition="dialog-bottom-transition"
    scrollable
    :max-width="dialogMaxWidth"
  >
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon v-if="titleIcon" :color="titleIconColor" class="mr-2">{{ titleIcon }}</v-icon>
        <div class="d-flex flex-column">
          <span v-if="title">{{ title }}</span>
          <span v-if="subtitle" class="text-subtitle-2 text-medium-emphasis">
            {{ subtitle }}
          </span>
        </div>
        <v-spacer />
        <v-btn
          v-if="!hideCloseButton"
          color="on-primary"
          icon
          variant="text"
          :disabled="anyLoading"
          @click="doClose()"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider class="mb-2" />
      <v-card-text>
        <slot name="content" />
      </v-card-text>
      <v-divider v-if="showCancel || subActionName || !hideActionButton" />
      <v-card-actions v-if="showCancel || subActionName || !hideActionButton" class="px-4 py-3">
        <v-spacer />
        <v-btn
          v-if="showCancel"
          color="grey"
          class="text-on-primary"
          variant="outlined"
          :disabled="anyLoading"
          :data-test="cancelDataTest || undefined"
          @click="$emit('close')"
        >
          {{ cancelBtn }}
        </v-btn>
        <v-btn
          v-if="subActionName"
          variant="flat"
          :loading="resolvedSubActionLoading"
          :disabled="anyLoading || actionDisabled"
          :color="subActionColor"
          class="text-on-primary"
          @click="doSubAction()"
        >
          {{ subActionName }}
        </v-btn>
        <v-btn
          v-if="!hideActionButton"
          :loading="resolvedActionLoading"
          :disabled="anyLoading || actionDisabled"
          color="primary"
          variant="flat"
          class="text-on-primary"
          @click="doAction()"
          :href="actionLink"
          :data-test="actionDataTest || undefined"
          target="_blank"
        >
          {{ actionName }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts" setup>
import { TranslateResult } from "vue-i18n";
import { computed, watch, ref } from "vue";
import i18n from "@/app/plugins/i18n";

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  prominent: {
    type: Boolean,
    default: false,
  },
  hideActionButton: {
    type: Boolean,
    default: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  actionLoading: {
    type: Boolean,
    default: false,
  },
  subActionLoading: {
    type: Boolean,
    default: false,
  },
  actionDisabled: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: "",
  },
  subtitle: {
    type: String,
    default: "",
  },
  titleIcon: {
    type: String,
    default: "",
  },
  titleIconColor: {
    type: String,
    default: "",
  },
  actionName: {
    type: String,
    default: "",
  },
  actionLink: {
    type: String,
    default: "",
  },
  actionDataTest: {
    type: String,
    default: "",
  },
  subActionName: {
    type: String,
    default: "",
  },
  subActionColor: {
    type: String,
    default: "green",
  },
  buttonColor: {
    type: String,
    default: "",
  },
  cancelName: {
    type: String,
    default: "",
  },
  cancelDataTest: {
    type: String,
    default: "",
  },
  fixedWidth: {
    type: String,
    required: false,
  },
  showCancel: {
    type: String,
    required: false,
  },
  hideCloseButton: {
    type: Boolean,
    default: false,
  },
});

const dialog = ref(props.show);
const dialogMaxWidth = computed(() => props.fixedWidth || "50%");
const resolvedActionLoading = computed(() => props.actionLoading || props.isLoading);
const resolvedSubActionLoading = computed(() => props.subActionLoading || props.isLoading);
const anyLoading = computed(() => props.isLoading || props.actionLoading || props.subActionLoading);
const cancelBtn = computed((): TranslateResult => {
  return props.cancelName ? props.cancelName : i18n.global.t("common.action.cancel");
});
watch(
  () => props.show,
  (newVal) => {
    onShowChanged(newVal);
  },
);
const emit = defineEmits(["close", "action", "subAction"]);
const doClose = () => {
  emit("close");
};
const doAction = () => {
  emit("action");
};
const doSubAction = () => {
  emit("subAction");
};
const onShowChanged = (newVal: boolean): void => {
  dialog.value = newVal;
};
</script>
